import {
  DATA_GRID_SEARCH_PARAMS_ID,
  DataGridSearchParams,
  PAGE_SEARCH_PARAMS_ID,
} from '../../constants/urlSync';
import {dataGridsParser, pageParser} from './parsers';

const readParam = (key: string): string =>
  new URL(window.location.href).searchParams.get(key) ?? '';

/**
 * @about Utility function to get dataGrid info from URL
 * @returns DataGridSearchParams
 * @example
 *  URL = http://metada.localhost:4200/vehicles?dataGrids=vehicle,6f5fc539-...,1|
 *
 *  const returnValue = getSearchParamsByGridCode('vehicle');
 *  //=> {gridCode: 'vehicle', queryId: '6f5fc539-...', rowIndex: '1'}
 */
export const getSearchParamsByGridCode = (gridCode: string): DataGridSearchParams => {
  const formattedSearchParams = getFormattedSearchParams();
  return formattedSearchParams[gridCode];
};

/**
 * @about Utility function to get dataGrid info from URL.
 *
 * Supports two URL formats (auto-detected by `dataGridsParser`):
 * - **New (pipe-separated):** `dataGrids=vehicle,abc,1|customers,def,2|`
 * - **Legacy (comma-only):** `dataGrids=vehicle,abc,1`
 *
 * Page is stored separately in `&page=gridCode:N,...`.
 *
 * @returns Record keyed by gridCode
 */
export const getFormattedSearchParams = (): Record<string, DataGridSearchParams> =>
  dataGridsParser.parse(readParam(DATA_GRID_SEARCH_PARAMS_ID)) ?? {};

/**
 * Serializes all grid search params into a flat URL-safe string via `dataGridsParser`.
 * Output format: `vehicle,abc,1|customers,def,2|` (trailing pipe always appended).
 * Filters invalid entries (empty/`undefined` queryId).
 */
export const convertToSearchParams = (
  newSearchParams: Record<string, DataGridSearchParams>,
  omitSearchParams?: boolean
): Record<string, string> => {
  const serialized = dataGridsParser.serialize(newSearchParams);

  if (omitSearchParams) {
    return {[DATA_GRID_SEARCH_PARAMS_ID]: serialized};
  }

  return {
    ...Object.fromEntries(new URLSearchParams(window.location.search)),
    [DATA_GRID_SEARCH_PARAMS_ID]: serialized,
  };
};

/**
 * Reads the current 1-based page number for a specific grid from the `&page=` URL param.
 *
 * Format: `page=vehicle-opensearch:2,customers:1`
 *
 * @returns 1-based page number, or undefined if not present
 */
export const getPageFromUrl = (gridCode: string): number | undefined => {
  const pages = pageParser.parse(readParam(PAGE_SEARCH_PARAMS_ID));
  return pages?.[gridCode];
};

/**
 * Writes the 1-based page number for a specific grid into the `&page=` URL param via replaceState.
 *
 * Format: `page=vehicle-opensearch:2,customers:1`
 *
 * Other grids' page entries are preserved. The `dataGrids` param is not touched.
 * Skips replaceState when the URL already has the correct page value.
 */
export const setPageInUrl = (gridCode: string, page: number): void => {
  const pages = pageParser.parse(readParam(PAGE_SEARCH_PARAMS_ID)) ?? {};

  if (pages[gridCode] === page) {
    return;
  }

  pages[gridCode] = page;

  const url = new URL(window.location.href);
  const allParams = Object.fromEntries(new URLSearchParams(window.location.search));
  allParams[PAGE_SEARCH_PARAMS_ID] = pageParser.serialize(pages);

  window.history.replaceState(
    null,
    '',
    url.origin + url.pathname + '?' + new URLSearchParams(allParams)
  );
};
